/**
 * Physical + perspective world scale.
 *
 * finalWidthFrac =
 *   physicalSizeAtDepth(y)
 *   × readabilityMultiplier
 *   × editorScale (1.0 = recommended)
 *
 * Fence baked into each backyard master is the primary length reference.
 * Perspective uses ground Y so nearer objects read larger than far ones.
 */

import type { PresenceCameraConfig } from '@/world-prototype/types';

/** Scene ids that carry backyard fence calibration. */
export type YardScaleSceneId = 'hubert' | 'raleigh' | 'spanish-fork';

export interface YardScaleCalibration {
  id: YardScaleSceneId;
  /** Real-world fence height represented in the master art. */
  fenceHeightFeet: number;
  /** World-Y of the fence ground line (approx). */
  fenceY: number;
  /**
   * Apparent fence height as a fraction of world HEIGHT at fenceY.
   * Measured against the painted fence in the master plate.
   */
  fenceHeightWorldFrac: number;
  /** Near (foreground / porch) Y and relative scale vs fence. */
  nearY: number;
  farY: number;
  /** Perspective multipliers at near/far (1 = fence depth). */
  nearScale: number;
  farScale: number;
  /** Big Sister authored height (feet). */
  characterHeightFeet: number;
  /**
   * Optional storybook readability multiplier for the presence player.
   * Defaults to CHARACTER_DISPLAY_SCALE when omitted.
   */
  characterDisplayScale?: number;
}

/**
 * Recommended physical sizes (feet). Not construction-precise —
 * believable children's-book proportions.
 */
export const PHYSICAL_SIZE_FEET = {
  bigSister: 3.9,
  littleBrother: 3.15,
  grandma: 5.1,
  bucket: 1.25,
  pinecone: 0.4,
  rabbit: 1.1,
  /** Small backyard frog — clearly smaller than the rabbit. */
  frog: 0.5,
  /** Small ground-nesting bird — plover-sized, ankle-high on a child. */
  bird: 0.62,
  gardenBedHeight: 1.4,
  gardenBedWidth: 6.0,
  gasGrill: 3.7,
  pizzaOven: 2.6,
  doghouse: 3.0,
  acUnit: 2.75,
  patioChair: 3.0,
  swingSetHeight: 8.0,
  hammockWidth: 10.0,
  hammockHeight: 3.2,
  shedWall: 7.5,
  shedPeak: 9.5,
  clotheslineHeight: 5.5,
  clotheslineWidth: 12.0,
  firePit: 2.2,
  littlePine: 7.0,
  largeTree: 18.0,
  treeArtisticCompression: 0.72,
  /** Small ornamental bush — about waist-high on a child. */
  bush: 2.4,
  /** Wooden patio gazebo / roof structure. */
  gazebo: 9.0,
  /** Small backyard garden footbridge (rail height). */
  smallBridgeHeight: 3.4,
  /** Small backyard garden footbridge span. */
  smallBridgeWidth: 7.0,
} as const;

/** Controlled readability boost for tiny interactive toys. */
export const READABILITY = {
  pinecone: 1.55,
  /** Kid-readable yard bucket (~1.5× prior gameplay size). */
  bucket: 1.62,
  /**
   * A physically honest rabbit reads at ~22% of the drawn child and is easy to
   * miss. This lands it near 40% — unmistakably a small backyard rabbit, but
   * big enough for a 2–6 year old to spot and tap on a phone.
   */
  rabbit: 2.19,
  /**
   * Lands near half the rabbit's drawn height — unmistakably the smaller animal,
   * still easy for a 2–6 year old to spot on the grass.
   */
  frog: 2.35,
  /**
   * Same treatment as the rabbit, tuned so the bird lands near two thirds of
   * the rabbit's height: unmistakably the smaller animal, still big enough for
   * a 2–6 year old to spot guarding its nest.
   */
  bird: 2.65,
  default: 1,
} as const;

/**
 * Hubert — large yard, 6 ft fence.
 * Fence reads ~13.5% of plate height near mid-yard.
 */
export const HUBERT_SCALE: YardScaleCalibration = {
  id: 'hubert',
  fenceHeightFeet: 6,
  fenceY: 0.36,
  fenceHeightWorldFrac: 0.135,
  nearY: 0.88,
  farY: 0.30,
  nearScale: 1.22,
  farScale: 0.92,
  characterHeightFeet: PHYSICAL_SIZE_FEET.bigSister,
  /** ~12.5% smaller than the shared 1.9× display scale — less dominant in Hubert. */
  characterDisplayScale: 1.66,
};

/**
 * Holly Springs — smaller yard, 3 ft fence.
 * Fence is shorter in real life, so kids should read taller than it.
 */
export const RALEIGH_SCALE: YardScaleCalibration = {
  id: 'raleigh',
  fenceHeightFeet: 3,
  fenceY: 0.34,
  fenceHeightWorldFrac: 0.095,
  nearY: 0.86,
  farY: 0.28,
  nearScale: 1.18,
  farScale: 0.94,
  characterHeightFeet: PHYSICAL_SIZE_FEET.bigSister,
};

/**
 * Spanish Fork — open grass bowl, no fence in the master.
 * Uses the rear dirt berm as a soft depth reference until measured.
 */
export const SPANISH_FORK_SCALE: YardScaleCalibration = {
  id: 'spanish-fork',
  fenceHeightFeet: 3,
  fenceY: 0.34,
  fenceHeightWorldFrac: 0.08,
  nearY: 0.9,
  farY: 0.32,
  nearScale: 1.16,
  farScale: 0.94,
  characterHeightFeet: PHYSICAL_SIZE_FEET.grandma,
};

export function yardScaleFor(sceneId: string | null | undefined): YardScaleCalibration {
  if (sceneId === 'raleigh') return RALEIGH_SCALE;
  if (sceneId === 'spanish-fork') return SPANISH_FORK_SCALE;
  return HUBERT_SCALE;
}

/** Perspective multiplier at ground Y (1 ≈ fence depth). */
export function perspectiveScaleAtY(
  y: number,
  cal: YardScaleCalibration,
): number {
  const span = cal.nearY - cal.farY || 1;
  const t = clamp01((y - cal.farY) / span);
  return lerp(cal.farScale, cal.nearScale, t);
}

/** World-height fraction for a real-world height at ground Y. */
export function feetToHeightFrac(
  feet: number,
  y: number,
  cal: YardScaleCalibration,
): number {
  if (feet <= 0) return 0;
  const atFence =
    (feet / cal.fenceHeightFeet) * cal.fenceHeightWorldFrac;
  const persp =
    perspectiveScaleAtY(y, cal) / perspectiveScaleAtY(cal.fenceY, cal);
  return atFence * persp;
}

/** World-width fraction for a real-world width at ground Y (isotropic feet). */
export function feetToWidthFrac(
  feet: number,
  y: number,
  cal: YardScaleCalibration,
  worldAspect: number,
): number {
  const aspect = worldAspect > 0 ? worldAspect : 16 / 9;
  return feetToHeightFrac(feet, y, cal) / aspect;
}

export interface PhysicalScaleInput {
  /** Prefer height for upright props. */
  physicalHeightFeet?: number;
  /** Prefer width for wide/low props (hammock, clothesline). */
  physicalWidthFeet?: number;
  /** Sprite pixel aspect height/width. */
  imageAspect: number;
  y: number;
  worldAspect: number;
  sceneId: string;
  /** Editor / authored multiplier. 1 = recommended. */
  editorScale?: number;
  readabilityMultiplier?: number;
  /** Artistic compression for large trees (0–1). */
  artisticCompression?: number;
}

/**
 * Resolve scenery layer widthFrac from physical size + depth + editor scale.
 */
export function resolvePhysicalWidthFrac(input: PhysicalScaleInput): number {
  const cal = yardScaleFor(input.sceneId);
  const editorScale = input.editorScale ?? 1;
  const readability = input.readabilityMultiplier ?? 1;
  const compression = input.artisticCompression ?? 1;
  const aspect = input.worldAspect > 0 ? input.worldAspect : 16 / 9;
  const imgAspect = input.imageAspect > 0 ? input.imageAspect : 1;

  let widthFrac: number;
  if (input.physicalWidthFeet != null && input.physicalWidthFeet > 0) {
    widthFrac = feetToWidthFrac(
      input.physicalWidthFeet,
      input.y,
      cal,
      aspect,
    );
  } else if (input.physicalHeightFeet != null && input.physicalHeightFeet > 0) {
    const heightFrac = feetToHeightFrac(
      input.physicalHeightFeet,
      input.y,
      cal,
    );
    widthFrac = heightFrac / (imgAspect * aspect);
  } else {
    return 0;
  }

  return Math.max(0.004, widthFrac * readability * compression * editorScale);
}

/** CSS --wp-char-width (0–1) before depth scale multiplier. */
/**
 * Storybook readability multiplier for presence player sprites.
 * Applied on top of physical feet → world-frac sizing.
 */
export const CHARACTER_DISPLAY_SCALE = 1.9;

export function characterBaseWidthFrac(
  cal: YardScaleCalibration,
  worldAspect: number,
  imageAspect = 1318 / 812,
): number {
  // Size so that at fence depth, effective height matches characterHeightFeet.
  // Presence applies depthScale separately — bake fence-depth into base using
  // the same near/far curve as props (perspectiveScaleAtY), then divide by
  // the camera depthScale at fence so final on-screen size matches.
  const heightAtFence = feetToHeightFrac(
    cal.characterHeightFeet,
    cal.fenceY,
    cal,
  );
  const widthAtFence = heightAtFence / (imageAspect * (worldAspect || 16 / 9));
  const displayScale = cal.characterDisplayScale ?? CHARACTER_DISPLAY_SCALE;
  return widthAtFence * displayScale;
}

/**
 * Map yard perspective calibration onto PresenceCameraConfig depth fields
 * so character depth scaling stays in family with prop perspective.
 */
export function cameraDepthFromYardScale(
  cal: YardScaleCalibration,
): Pick<
  PresenceCameraConfig,
  'depthScaleNear' | 'depthScaleFar' | 'depthYNear' | 'depthYFar'
> {
  // Camera multiplies CSS base width. Normalize so fence depth ≈ 1.0.
  const fencePersp = perspectiveScaleAtY(cal.fenceY, cal);
  return {
    depthYNear: cal.nearY,
    depthYFar: cal.farY,
    depthScaleNear: cal.nearScale / fencePersp,
    depthScaleFar: cal.farScale / fencePersp,
  };
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Lookup helpers for known object ids / kinds. */
export function physicalSizeForObjectId(id: string): {
  physicalHeightFeet?: number;
  physicalWidthFeet?: number;
  readabilityMultiplier?: number;
  artisticCompression?: number;
} | null {
  if (id === 'bucket') {
    return {
      physicalHeightFeet: PHYSICAL_SIZE_FEET.bucket,
      readabilityMultiplier: READABILITY.bucket,
    };
  }
  if (id.startsWith('pinecone')) {
    return {
      physicalHeightFeet: PHYSICAL_SIZE_FEET.pinecone,
      readabilityMultiplier: READABILITY.pinecone,
    };
  }
  if (id === 'garden-bed') {
    return { physicalHeightFeet: PHYSICAL_SIZE_FEET.gardenBedHeight };
  }
  if (id === 'gas-grill') {
    return { physicalHeightFeet: PHYSICAL_SIZE_FEET.gasGrill };
  }
  if (id === 'electric-pizza-oven') {
    return { physicalHeightFeet: PHYSICAL_SIZE_FEET.pizzaOven };
  }
  if (id === 'swingset-slide') {
    return { physicalHeightFeet: PHYSICAL_SIZE_FEET.swingSetHeight };
  }
  if (id === 'hammock') {
    return { physicalWidthFeet: PHYSICAL_SIZE_FEET.hammockWidth };
  }
  if (id === 'clothesline') {
    return { physicalWidthFeet: PHYSICAL_SIZE_FEET.clotheslineWidth };
  }
  if (id === 'fire-pit') {
    return { physicalHeightFeet: PHYSICAL_SIZE_FEET.firePit };
  }
  if (id === 'shed') {
    return { physicalHeightFeet: PHYSICAL_SIZE_FEET.shedPeak };
  }
  if (id.startsWith('little-pine')) {
    return {
      physicalHeightFeet: PHYSICAL_SIZE_FEET.littlePine,
      artisticCompression: PHYSICAL_SIZE_FEET.treeArtisticCompression,
    };
  }
  if (
    id === 'tree-left' ||
    id === 'tree-right' ||
    id.startsWith('holly-springs-large-tree') ||
    id.startsWith('spanish-fork-large-tree')
  ) {
    return {
      physicalHeightFeet: PHYSICAL_SIZE_FEET.largeTree,
      artisticCompression: PHYSICAL_SIZE_FEET.treeArtisticCompression,
    };
  }
  if (id.startsWith('spanish-fork-bush') || id.startsWith('bush')) {
    return { physicalHeightFeet: PHYSICAL_SIZE_FEET.bush };
  }
  if (id === 'gazebo' || id === 'patio-roof') {
    return { physicalHeightFeet: PHYSICAL_SIZE_FEET.gazebo };
  }
  if (id === 'small-bridge') {
    return { physicalWidthFeet: PHYSICAL_SIZE_FEET.smallBridgeWidth };
  }
  if (id.startsWith('holly-springs-ac-unit') || id === 'ac-unit') {
    return { physicalHeightFeet: PHYSICAL_SIZE_FEET.acUnit };
  }
  return null;
}
